import { ICoordinatingGroup, ICountry, IInstitution, IMasterGroup, ISourceDataNote, ITrialFormMetadata, NoteStatus, NoteType } from "@/api/dtos";
import { FC, useCallback, useMemo } from "react";
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { alpha, Box, Chip, colors, styled, useTheme } from "@mui/material";
import VirtualizedTable, { IVirtualizedTableColumn } from "@/components/layouts/VirtualizedTable";
import { DateTime } from "luxon";
import SourceDataSubnote from "../../sourceData/common/SourceDataSubnote";
import { red } from "@mui/material/colors";
import { useMeridiemMode } from "@mui/x-date-pickers/internals/hooks/date-helpers-hooks";

interface IDiscrepancyNoteGridProps {
    notes?: ISourceDataNote[];
    metadata?: ITrialFormMetadata;
    masterGroups?: IMasterGroup[];
    coordinatingGroups?: ICoordinatingGroup[];
    countries?: ICountry[];
    institutions?: IInstitution[];
    loading?: boolean;
    updating?: boolean;
    patientView?: boolean;
    settingsOpen?: boolean;
    onSettingsClose?: () => void;
}

const SubnoteBox = styled(Box)(({theme}) => ({
    borderRight: 
        theme.palette.mode === 'dark' ? 
            `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
            `solid 1px ${alpha(theme.palette.common.black, 0.12)}`
}));


const DiscrepancyNoteGrid: FC<IDiscrepancyNoteGridProps> = ({
    notes,
    metadata,
    countries,
    coordinatingGroups,
    masterGroups,
    institutions,
    loading,
    updating,
    patientView,
    onSettingsClose,
    settingsOpen
}) => {

    const theme = useTheme();
    
  


    const processedNotes = useMemo(() => {
        if (loading) {
            return [];
        }

   

        return notes?.map(x => {
            const institution = institutions?.find(y => y.id === x.institutionId);
            const country = countries?.find(y => y.id === institution?.countryId);
            const masterGroup = masterGroups?.find(y => y.id === institution?.masterGroupId);
            const coordinatingGroup = coordinatingGroups?.find(y => y.id === institution?.coordinatingGroupId);

            const event = metadata?.events?.find(y => y.id === x.eventMetadataId);
            const form = metadata?.forms?.find(y => y.id === x.formMetadataId);
            const version = form?.versions?.find(y => y.id === x.formVersionMetadataId);
            const section = version?.sections?.find(y => y.id === x.formSectionMetadataId);
            const field = version?.fields?.find(y => y.id === x.formFieldMetadataId);

            return {
                ...x,
                masterGroup,
                masterGroupName: masterGroup?.name,
                coordinatingGroup,
                coordinatingGroupName: coordinatingGroup?.name,
                country,
                countryName: country?.name,
                institution,
                institutionName: institution?.name,
                event,
                eventName: event?.name + (event?.repeating ? ` (${x.eventRepeat})` : ''),
                form,
                version,
                formName: form?.name + (version ? ` (${version.name})` : ''),
                section,
                sectionName: section ? section?.name + (section?.repeating ? ` (${x.formSectionRepeat})` : '') : undefined,
                field,
                fieldName: field?.name,
                title: x.notes?.find(y => true)?.title
            };
        }) ?? [];
    }, [masterGroups, coordinatingGroups, countries, institutions, metadata, notes, loading])

    const baseHeaderStyles = useMemo(() => ({
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        backgroundColor: theme.palette.mode === 'dark' ? colors.grey[900] : 'white',
        //whiteSpace: "nowrap"
    }
    ), [theme]);
    const columns = useMemo(() => {

        const baseCellStyles = {
            overflow: 'hidden',
            textOverflow: 'ellipsis',

            
            //whiteSpace: "nowrap"
        }

        const c: IVirtualizedTableColumn[] = [
            {
                field: 'eventName',
                headerName: 'Event',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 150
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 150,
                }
            },
            {
                field: 'formName',
                headerName: 'Form',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 250
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 250,
                }
            },
            {
                field: 'sectionName',
                headerName: 'Section',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 150
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 150,
                }
            },
            {
                field: 'fieldName',
                headerName: 'Field',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 250
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 250,
                }
            },
            {
                field: 'type',
                headerName: 'Type',
                getter: data => {
                    switch (data. type) {
                        case NoteType.Query:
                            return 'Query';
                        case NoteType.ValidationFailed:
                            return 'Validation Failure';
                        case NoteType.Internal:
                            return 'Internal';
                        case NoteType.ProtocolDeviation:
                            return 'Protocol Deviation';
                        case NoteType.ImportantProtocolDeviation:
                            return 'Important Protocol Deviation';
                        case NoteType.Annotation:
                            return 'Annotation';
                        case NoteType.ReasonForChange:
                            return 'Reason For Change';
                        default:
                            return null;
                    }
                },
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 180
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 180,
                }
            },
            {
                field: 'status',
                headerName: 'Status',
                getter: data => {
                    switch (data.status) {
                        case NoteStatus.New:
                            return 'New';
                        case NoteStatus.Updated:
                            return 'Update';
                        case NoteStatus.ResolutionProposed:
                            return 'Resolution Proposed';
                        case NoteStatus.Closed:
                            return 'Closed';
                        default:
                            return null;
                    }
                },
                cell: (data, value) => {
                    switch (data.status) {
                        case NoteStatus.New:
                            return <Chip size="small" label="New" color="error" />;
                        case NoteStatus.Updated:
                            return <Chip size="small" label="Update" color="warning" />;
                        case NoteStatus.ResolutionProposed:
                            return <Chip size="small" label="Resolution Proposed" color="info" />;
                        case NoteStatus.Closed:
                            return <Chip size="small" label="Closed" color="success" />;
                        default:
                            return null;
                    }
                },
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 200
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 200,
                }
            },
            {
                field: 'title',
                headerName: 'Note',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 350
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 350,
                }
            },
            {
                field: 'creator',
                headerName: 'Creator',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 150
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 150,
                }
            },
            {
                field: 'createdDate',
                headerName: 'Created',
                sort: data => data.createdDate,
                getter: data => data.createdDate ? DateTime.fromISO(data.createdDate).toFormat('dd/MM/yyyy') : undefined,
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 150
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 150,
                }
            }
        ];

        if (patientView) {
            return c;
        }

        return [
            
            {
                field: 'masterGroupName',
                headerName: 'Master Group',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 180
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 180,
                }
            },
            {
                field: 'coordinatingGroupName',
                headerName: 'Coordinating Group',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 180
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 180,
                }
            },
            {
                field: 'countryName',
                headerName: 'Country',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 150
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 150,
                }
            },
            {
                field: 'institutionName',
                headerName: 'Institution',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 180
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 180,
                }
            },
            {
                field: 'patientStudyNumber',
                headerName: 'Participant',
                tableHeaderSx: {
                    ...baseHeaderStyles,
                    width: 130
                },
                tableCellSx: {
                    ...baseCellStyles,
                    width: 130,
                }
            }, 
            ...c
        ];
    }, [patientView,  baseHeaderStyles])

    const expandContent = useCallback((data: ISourceDataNote) => {
        return (
            <SubnoteBox
                sx={{
                    maxWidth: 'calc(100vw - 350px)',
                    pr: 2,
                }}
            >
                {
                    data.notes?.map((x, i) => {
                        return (
                            <SourceDataSubnote  key={i} {...x} createdDate={x.createdDate} last={(i + 1) === data.notes?.length} />
                        );
                    })
                }
            </SubnoteBox>
        )
    }, [])

    return (
        <VirtualizedTable
            columns={columns}
            data={processedNotes}
            loading={loading || updating}
            onSettingsClose={onSettingsClose}
            settingsOpen={settingsOpen}
            expandContent={expandContent}
        />
    );
};

export default DiscrepancyNoteGrid;